import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import Papa from 'papaparse';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import './mobile.css';
import BoardArena from './components/BoardArena';
import DraftTutorial from './components/DraftTutorial';
import MainMenu from './components/MainMenu';
import FeedbackButton from './components/FeedbackButton';
import GameViewport from './components/GameViewport';
import MenuBg, { MENU_OVERLAY_BG } from './components/MenuBg';
import useMobileSetup from './hooks/useMobileSetup';
import { loadChallengeProgress, saveChallengeProgress, clearChallengeProgress, saveSessionView, clearSessionView, resolveInitialView } from './utils/challengeStorage';
import { formatChallengeMatchup } from './config/challengeConfig';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Capacitor } from '@capacitor/core';

// Lazy-load screens that are never needed during a match.
// webpackPrefetch: browser silently downloads these chunks during idle time
// after the main bundle loads — so navigation is instant instead of showing
// a "Loading…" spinner.
const PlayerCards          = lazy(() => import(/* webpackPrefetch: true */ './components/PlayerCards'));
const Kickoppedia          = lazy(() => import(/* webpackPrefetch: true */ './components/Kickoppedia'));
const WhatsNext            = lazy(() => import(/* webpackPrefetch: true */ './components/WhatsNext'));
const ActionCardCollection = lazy(() => import(/* webpackPrefetch: true */ './components/ActionCardCollection'));
const ChallengeLanding     = lazy(() => import(/* webpackPrefetch: true */ './components/ChallengeLanding'));
const ChallengeSquadView   = lazy(() => import(/* webpackPrefetch: true */ './components/ChallengeSquadView'));
const ChallengeActionCards = lazy(() => import(/* webpackPrefetch: true */ './components/ChallengeActionCards'));
const ChallengeReward      = lazy(() => import(/* webpackPrefetch: true */ './components/ChallengeReward'));
const ChallengeActionCardReward = lazy(() => import(/* webpackPrefetch: true */ './components/ChallengeActionCardReward'));

const CHALLENGE_VIEWS = ['challenge', 'challenge-squad', 'challenge-action-cards', 'challenge-reward', 'challenge-action-reward'];


// Squad position limits (mirrors PlayerCards.jsx constants)
const SQUAD_GROUPS_APP = [
  { types: ['Keeper'],                       limit: 4  },
  { types: ['DefenderCenter'],               limit: 10 },
  { types: ['DefenderWide'],                 limit: 6  },
  { types: ['MidfielderCenter'],             limit: 10 },
  { types: ['MidfielderWide'],               limit: 6  },
  { types: ['ForwardCenter', 'ForwardWide'], limit: 8  },
];

const checkSquadComplete = (squad) =>
  SQUAD_GROUPS_APP.every(g => squad.filter(p => g.types.includes(p.CardType)).length === g.limit);

// ── Error boundary — catches ChunkLoadError after a new Vercel deploy ────────
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    // ChunkLoadError: stale JS chunks after a new deployment.
    // Force a full reload to pick up the latest build.
    if (error && (error.name === 'ChunkLoadError' || /Loading chunk/.test(error.message))) {
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#94a3b8',
        }}>
          <p style={{ fontSize: '24px', marginBottom: '16px' }}>⚠️ Something went wrong loading this page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 32px', fontSize: '18px', borderRadius: '10px',
              background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [view, setView] = useState(resolveInitialView);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAbandonWarning, setShowAbandonWarning] = useState(false);
  const [pendingView, setPendingView] = useState(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [showGameModeModal, setShowGameModeModal] = useState(false);
  const [gameMode, setGameMode] = useState('basic');
  const [actionMode, setActionMode] = useState('basic'); // 'basic' | 'individual'
  const [squadData, setSquadData] = useState(null);
  const [soloMode, setSoloMode] = useState(false);
  const [aiPlayer, setAiPlayer] = useState(null);
  const [showHomeAwayModal, setShowHomeAwayModal] = useState(false);
  const [tournamentData, setTournamentData] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);
  const [pendingCpuPlayer, setPendingCpuPlayer] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedMatchMode, setSelectedMatchMode] = useState('win'); // 'win' | 'superwin'
  const [verticalBoard, setVerticalBoard] = useState(true);
  const [cardModalReturn, setCardModalReturn] = useState(false); // true when cards/action-cards opened from game mode modal
  const [showSoloSquadWarning, setShowSoloSquadWarning] = useState(false);
  // Sound temporarily disabled for this version — restore the original initializer to re-enable:
  // const [soundEnabled, setSoundEnabled] = useState(() => {
  //   const saved = localStorage.getItem('kickopp-sound-enabled');
  //   return saved !== null ? JSON.parse(saved) : true;
  // });
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [challengeProgress, setChallengeProgress] = useState(() => {
    const p = loadChallengeProgress();
    // Stale "Trial 9" state (challenge already fully completed) — auto-clear on load
    if (p && p.level > 8) { clearChallengeProgress(); return null; }
    return p;
  });
  const [showDraftTutorial, setShowDraftTutorial] = useState(false); // set true when arena starts, if not yet seen
  const [challengeMatchSquad, setChallengeMatchSquad] = useState(null);
  const [challengeMatchActionCards, setChallengeMatchActionCards] = useState(null);
  const [showChallengeLeaveWarning, setShowChallengeLeaveWarning] = useState(false);
  const [challengeLeaveTarget, setChallengeLeaveTarget] = useState(null);

  // Initialize mobile-specific features (Capacitor)
  useMobileSetup();

  // Lock to landscape during the game board, portrait for all other screens
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    ScreenOrientation.lock({ orientation: view === 'arena' ? 'landscape' : 'portrait' }).catch(() => {});
  }, [view]);

  // Persist view so the browser can restore the exact screen after a refresh / tab close
  useEffect(() => { saveSessionView(view); }, [view]);

  // Warn on browser close / refresh while in a challenge-critical screen
  useEffect(() => {
    const isCritical = !!challengeProgress &&
      ['arena', 'challenge-reward', 'challenge-action-reward', 'challenge-action-cards', 'challenge-squad'].includes(view);
    if (!isCritical) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [view, challengeProgress]);

  useEffect(() => {
    Papa.parse('/Tournament.csv', {
      download: true, header: true, skipEmptyLines: true,
      complete: (r) => setTournamentData(r.data.filter(row => row.Stage))
    });
    // Pre-warm the browser's HTTP cache for both card CSVs so that when
    // PlayerCards / ActionCardCollection / ChallengeActionCards open them,
    // the fetch is already cached and returns instantly instead of showing
    // a loading state.
    fetch('/PlayersDataUnityImport.csv').catch(() => {});
    fetch('/Action%20Cards.csv').catch(() => {});
  }, []);

  const hasActiveChallenge = !!challengeProgress?.squad;

  const menuItems = [
    { id: 'menu',   label: 'Main Menu' },
    { id: 'arena',  label: 'Play' },
    { id: 'challenge', label: 'Play Challenge' },
    // Challenge-specific cards — only when a challenge run is active
    ...(hasActiveChallenge ? [
      { id: 'challenge-squad',        label: '🏆 Challenge Squad',    group: 'challenge' },
      { id: 'challenge-action-cards', label: '🏆 Challenge Cards',     group: 'challenge' },
    ] : []),
    // Classic Play cards — always available (for PvP / Solo squad setup)
    { id: 'cards',        label: hasActiveChallenge ? 'Classic — Player Cards'  : 'Cards Collection' },
    { id: 'action-cards', label: hasActiveChallenge ? 'Classic — Action Cards'  : 'Action Cards' },
    { id: 'kickoppedia',  label: 'Kickoppedia' },
    { id: 'whatsnext',    label: "What's Next" },
  ];

  // Challenge screens the player shouldn't leave silently
  const CHALLENGE_FLOW = ['challenge', 'challenge-squad', 'challenge-action-cards', 'challenge-reward', 'challenge-action-reward'];

  const handleMenuItemClick = (viewId) => {
    // ── Arena: show abandon-match warning ──────────────────────────────────
    if (view === 'arena' && viewId !== 'arena' && !gameEnded) {
      setPendingView(viewId);
      setShowAbandonWarning(true);
      setMenuOpen(false);
    // ── Challenge flow: warn before leaving to a non-challenge screen ──────
    } else if (
      challengeProgress &&
      CHALLENGE_FLOW.includes(view) && view !== 'challenge' &&
      !CHALLENGE_FLOW.includes(viewId)
    ) {
      setChallengeLeaveTarget(viewId);
      setShowChallengeLeaveWarning(true);
      setMenuOpen(false);
    } else if (viewId === 'arena' && view !== 'arena') {
      // Show game mode selection modal before entering arena
      setMenuOpen(false);
      setShowGameModeModal(true);
    } else {
      setView(viewId);
      setMenuOpen(false);
      setCardModalReturn(false); // clear modal-return mode on any normal navigation
      if (viewId !== 'arena') {
        setGameEnded(false);
      }
    }
  };

  const handleSelectGameMode = (mode, acMode, solo = false, soloCpuPlayer = null, stage = null) => {
    const rawSquads = localStorage.getItem('kickopp-squads');
    const squads = rawSquads ? JSON.parse(rawSquads) : { 'Player 1': [], 'Player 2': [] };
    setGameMode(mode);
    setActionMode(acMode);
    setSquadData(squads);
    setSoloMode(solo);
    setAiPlayer(soloCpuPlayer);
    setSelectedStage(stage);
    // Clear any lingering challenge context so a friendly Play match never
    // shows Challenge Trial badges, Super Win banners, or challenge buttons.
    setChallengeMatchSquad(null);
    setChallengeMatchActionCards(null);
    setShowGameModeModal(false);
    // Show draft guide every new game session
    setShowDraftTutorial(true);
    setView('arena');
  };

  const handleStartSolo = (cpuPlayer) => {
    setShowHomeAwayModal(false);
    setPendingCpuPlayer(cpuPlayer);
    setSelectedStage(null);        // clear any previous difficulty pick
    setSelectedMatchMode('win');   // reset to default Win mode
    setShowStageModal(true);
  };

  const handleStartWithStage = (stage) => {
    setShowStageModal(false);
    handleSelectGameMode(gameMode, actionMode, true, pendingCpuPlayer, stage);
  };

  const handleCancelGameMode = () => {
    setShowGameModeModal(false);
  };

  const handleGoToCardsCollection = () => {
    setShowSoloSquadWarning(false);
    setShowGameModeModal(false);
    setView('cards');
  };

  const updateChallengeProgress = useCallback((updates) => {
    setChallengeProgress(prev => {
      const next = { ...prev, ...updates };
      saveChallengeProgress(next);
      return next;
    });
  }, []);

  const resetChallengeProgress = useCallback(() => {
    clearChallengeProgress();
    clearSessionView();
    setChallengeProgress(null);
    setChallengeMatchSquad(null);
    setChallengeMatchActionCards(null);
  }, []);

  const handleStartChallengeMatch = useCallback((progress) => {
    const { squad, enabledActionCards, boardLayout, level } = progress;
    setChallengeMatchSquad(squad);
    setChallengeMatchActionCards(enabledActionCards || []);
    setSquadData(prev => ({ ...(prev || {}), 'Player 1': squad }));

    // Write challenge action cards to localStorage so BoardArena reads them in individual mode
    const rawAC = localStorage.getItem('kickopp-action-selections');
    const acSelections = rawAC ? JSON.parse(rawAC) : { 'Player 1': [], 'Player 2': [] };
    acSelections['Player 1'] = enabledActionCards || [];
    localStorage.setItem('kickopp-action-selections', JSON.stringify(acSelections));

    setGameMode('squads');
    setActionMode(enabledActionCards?.length > 0 ? 'individual' : 'basic');
    setSoloMode(true);
    setVerticalBoard(boardLayout === 'vertical');
    // Use Tournament stage matching the challenge level so CPU uses level-filtered players
    const stage = tournamentData.find(r => r.Stage === String(level)) || tournamentData[0] || null;
    setSelectedStage(stage);
    // Home → manager plays as Player 1 (CPU = Player 2); Away → manager plays as Player 2 (CPU = Player 1)
    setAiPlayer(stage?.Side?.trim() === 'Away' ? 1 : 2);
    if (!localStorage.getItem('kickopp-tutorial-draft-seen')) {
      setShowDraftTutorial(true);
    }
    setView('arena');
  }, [tournamentData]);

  // Plain function (not useCallback) so it always closes over the current `view`/
  // `gameEnded` inside handleMenuItemClick — a memoized version with stale deps would
  // freeze on the mount-time `view`, occasionally misfiring the abandon-match warning
  // even though the match has actually already ended.
  const handleChallengeEnd = (result) => {
    const progress = loadChallengeProgress();
    if (!progress) return;

    const { difficulty, lives, level } = progress;
    const levelNum = level ?? 1;

    // Determine if the win type meets the trial's requirement
    const meetsRequirement = result.win && (
      difficulty === 'easy' ? true :
      difficulty === 'medium' ? (levelNum <= 4 ? true : result.superWin) :
      /* hard */ result.superWin
    );

    if (meetsRequirement) {
      // Trial 8 = final trial — no reward screen needed; cards are kept, run is over.
      if (levelNum >= 8) {
        resetChallengeProgress();   // resets challengeMatchSquad too
        handleMenuItemClick('challenge');
        return;
      }
      // Reset medium's per-trial retry counter for the next trial
      const livesForNext = difficulty === 'medium' ? 1 : lives;
      const updated = { ...progress, rewardPending: 'player-cards', lives: livesForNext };
      saveChallengeProgress(updated);
      setChallengeProgress(updated);
      // Clear the squad now — handleStartChallengeMatch will re-populate it
      // when the next trial begins. Without this, the stale squad leaks into
      // any friendly Play match the user starts before continuing the challenge.
      setChallengeMatchSquad(null);
      setChallengeMatchActionCards(null);
      // skipToChallenge: user chose "Challenge Menu" on the end screen — record
      // the win and let the challenge landing's "Collect Rewards" button handle
      // the reward flow. Otherwise go straight to the reward screen.
      handleMenuItemClick(result.skipToChallenge ? 'challenge' : 'challenge-reward');
      return;
    }

    // Loss or insufficient win type — apply difficulty rule
    // result.skipToMenu: player chose "Menu" on the reset end-screen → go to main menu
    const dest = result.skipToMenu ? 'menu' : 'challenge';
    if (difficulty === 'easy') {
      // Deduct from global life pool; restart from Trial 1 when pool empties
      const newLives = (lives ?? 3) - 1;
      if (newLives <= 0) {
        const updated = { ...progress, level: 1, lives: 3 };
        saveChallengeProgress(updated);
        setChallengeProgress(updated);
      } else {
        const updated = { ...progress, lives: newLives };
        saveChallengeProgress(updated);
        setChallengeProgress(updated);
      }
      setChallengeMatchSquad(null);
      setChallengeMatchActionCards(null);
      handleMenuItemClick(dest);
    } else if (difficulty === 'medium') {
      const currentLives = lives ?? 1;
      if (currentLives > 0) {
        // Still have 1 retry — use it, stay on the same trial
        const updated = { ...progress, lives: 0 };
        saveChallengeProgress(updated);
        setChallengeProgress(updated);
      } else {
        // No retries left — restart from Trial 1, restore retry for trial 1
        const updated = { ...progress, level: 1, lives: 1 };
        saveChallengeProgress(updated);
        setChallengeProgress(updated);
      }
      setChallengeMatchSquad(null);
      setChallengeMatchActionCards(null);
      handleMenuItemClick(dest);
    } else {
      // Hard: full reset
      clearChallengeProgress();
      setChallengeProgress(null);
      setChallengeMatchSquad(null);
      setChallengeMatchActionCards(null);
      handleMenuItemClick(dest);
    }
  };

  const handleAbandonMatch = () => {
    // Stop gameplay music when abandoning match
    if (gameplayMusicRef.current) {
      gameplayMusicRef.current.pause();
      gameplayMusicRef.current.currentTime = 0;
      gameplayMusicRef.current = null;
    }
    setView(pendingView);
    setShowAbandonWarning(false);
    setPendingView(null);
  };

  const handleCancelAbandon = () => {
    setShowAbandonWarning(false);
    setPendingView(null);
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;

    // If turning sound OFF, immediately stop all playing audio
    if (!newValue) {
      if (menuMusicRef.current) {
        menuMusicRef.current.pause();
      }
      if (gameplayMusicRef.current) {
        gameplayMusicRef.current.pause();
      }
    }
    // If turning sound ON, the useEffect will handle restarting appropriate music
    else {
      const menuPages = ['menu', 'cards', 'action-cards', 'kickoppedia', 'whatsnext', ...CHALLENGE_VIEWS];

      // Restart menu music if on a menu page
      if (menuPages.includes(view)) {
        if (menuMusicRef.current) {
          menuMusicRef.current.volume = 0.3;
          menuMusicRef.current.play().catch(() => {});
        }
      }
      // Restart gameplay music if in arena
      else if (view === 'arena') {
        if (gameplayMusicRef.current) {
          gameplayMusicRef.current.volume = 0.3;
          gameplayMusicRef.current.play().catch(() => {});
        }
      }
    }

    setSoundEnabled(newValue);
    localStorage.setItem('kickopp-sound-enabled', JSON.stringify(newValue));
  };

  // Fade out menu music gradually to 0 volume
  const fadeOutMenuMusic = () => {
    return new Promise((resolve) => {
      if (menuMusicRef.current && soundEnabled) {
        const startVolume = menuMusicRef.current.volume;
        const fadeStep = startVolume / 30; // 30 steps for smooth fade
        const fadeInterval = setInterval(() => {
          if (menuMusicRef.current.volume > fadeStep) {
            menuMusicRef.current.volume -= fadeStep;
          } else {
            menuMusicRef.current.volume = 0;
            menuMusicRef.current.pause();
            clearInterval(fadeInterval);
            resolve();
          }
        }, 50); // 50ms per step = ~1.5 seconds total fade
      } else {
        resolve();
      }
    });
  };

  // Start gameplay music 3 seconds after Play button is clicked
  const startGameplayMusic = () => {
    setTimeout(() => {
      // Sound temporarily disabled — restore localStorage check to re-enable:
      // const currentSoundEnabled = JSON.parse(localStorage.getItem('kickopp-sound-enabled') ?? 'true');
      const currentSoundEnabled = false;
      if (currentSoundEnabled && !gameplayMusicRef.current) {
        gameplayMusicRef.current = new Audio('/happy-relaxing-loop-275487.mp3');
        gameplayMusicRef.current.loop = true;
        gameplayMusicRef.current.volume = 0.3;
        gameplayMusicRef.current.preload = 'auto';

        // Improve loop synchronization by ensuring seamless playback
        gameplayMusicRef.current.addEventListener('ended', function() {
          this.currentTime = 0;
          this.play().catch(() => {});
        });

        gameplayMusicRef.current.play().catch(() => {});
      }
    }, 3000); // Start 3 seconds after Play button click
  };

  // Background music for menu pages (menu, cards, kickoppedia, whatsnext)
  const menuMusicRef = useRef(null);
  const gameplayMusicRef = useRef(null);
  useEffect(() => {
    const menuPages = ['menu', 'cards', 'action-cards', 'kickoppedia', 'whatsnext', ...CHALLENGE_VIEWS];

    if (menuPages.includes(view) && soundEnabled) {
      if (!menuMusicRef.current) {
        menuMusicRef.current = new Audio('/relaxing-guitar-loop-v5-245859.mp3');
        menuMusicRef.current.loop = true;
        menuMusicRef.current.volume = 0.3;
        menuMusicRef.current.preload = 'auto';

        // Improve loop synchronization by ensuring seamless playback
        menuMusicRef.current.addEventListener('ended', function() {
          this.currentTime = 0;
          this.play().catch(() => {});
        });
      }
      menuMusicRef.current.play().catch(() => {});
    } else {
      if (menuMusicRef.current) {
        menuMusicRef.current.pause();
      }
    }

    // Cleanup on unmount
    return () => {
      if (menuMusicRef.current && !menuPages.includes(view)) {
        menuMusicRef.current.pause();
        menuMusicRef.current = null;
      }
    };
  }, [view, soundEnabled]);

  const isArena = view === 'arena';
  // All pages use the same GameViewport 2160×1080 scaling as the arena
  const needsViewport = true;
  const appContent = (
    <div className={`App view-${view}`}>
        {/* Menu Button — hidden on main menu and cards/action-cards pages */}
      {view !== 'menu' && view !== 'cards' && view !== 'action-cards' && <button
        className="menu-button"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <div className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
        Menu
      </button>}

      {/* Dropdown Menu */}
      {menuOpen && (
        <>
          <div className="menu-overlay" onClick={() => setMenuOpen(false)}></div>
          <div className="menu-dropdown">
            <div className="menu-header">
              <h3>Navigation</h3>
              <button className="close-button" onClick={() => setMenuOpen(false)}>×</button>
            </div>
            <div className="menu-items">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  className={[
                    'menu-item',
                    item.group === 'challenge' ? 'menu-item--challenge' : '',
                    view === item.id ? 'active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleMenuItemClick(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* View Content */}
      {view === 'menu' && <MainMenu onNavigate={handleMenuItemClick} soundEnabled={soundEnabled} fadeOutMenuMusic={fadeOutMenuMusic} startGameplayMusic={startGameplayMusic} />}
      {view === 'arena' && showDraftTutorial && (
        <DraftTutorial
          onComplete={() => { localStorage.setItem('kickopp-tutorial-draft-seen', '1'); setShowDraftTutorial(false); }}
          onSkip={() => { localStorage.setItem('kickopp-tutorial-draft-seen', '1'); setShowDraftTutorial(false); }}
          isVertical={verticalBoard}
        />
      )}
      {view === 'arena' && <BoardArena soundEnabled={soundEnabled} onNavigate={handleMenuItemClick} gameplayMusicRef={gameplayMusicRef} setGameEnded={setGameEnded} gameMode={gameMode} actionMode={actionMode} squadData={squadData} isSolo={soloMode} aiPlayer={aiPlayer} verticalBoard={verticalBoard} cpuStage={selectedStage} challengeMode={!!challengeMatchSquad} onChallengeEnd={challengeMatchSquad ? handleChallengeEnd : null} challengeActionCardIds={challengeMatchActionCards} challengeDifficulty={challengeProgress?.difficulty} challengeLives={challengeProgress?.lives} requireSuperWin={!challengeMatchSquad && selectedMatchMode === 'superwin'} onShowTutorial={() => setShowDraftTutorial(true)} />}
      <AppErrorBoundary>
      <Suspense fallback={<div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Loading…</div>}>
        {view === 'cards' && <PlayerCards
          fromModal={cardModalReturn}
          onBack={() => {
            setCardModalReturn(false);
            if (cardModalReturn) setShowGameModeModal(true);
            else handleMenuItemClick('menu');
          }}
          onConfirm={() => { setCardModalReturn(false); setShowGameModeModal(true); }}
        />}
        {view === 'action-cards' && <ActionCardCollection
          fromModal={cardModalReturn}
          onBack={() => {
            setCardModalReturn(false);
            if (cardModalReturn) setShowGameModeModal(true);
            else handleMenuItemClick('menu');
          }}
          onConfirm={() => { setCardModalReturn(false); setShowGameModeModal(true); }}
        />}
        {view === 'kickoppedia' && <Kickoppedia />}
        {view === 'whatsnext' && <WhatsNext />}
        {view === 'challenge' && (
          <ChallengeLanding
            existingProgress={challengeProgress}
            onNavigate={handleMenuItemClick}
            onStartNew={(difficulty, boardLayout) => {
              resetChallengeProgress();
              // Clear "super-win popup already seen" flags so the popup shows on the
              // first super-win-required trial of the new run.
              try {
                localStorage.removeItem('kickopp-superwin-popup-seen-medium');
                localStorage.removeItem('kickopp-superwin-popup-seen-hard');
              } catch (e) {}
              const newProgress = { level: 1, difficulty, boardLayout, lives: difficulty === 'easy' ? 3 : difficulty === 'medium' ? 1 : 0, squad: null, cardTypeCounts: null, actionCards: null, enabledActionCards: null, draftedRewardIds: [], rewardPending: false };
              setChallengeProgress(newProgress);
              handleMenuItemClick('challenge-squad');
            }}
            onContinue={() => {
              // If rewards are pending from a completed trial, go to the correct
              // reward screen first — don't re-start the same trial from scratch.
              if (challengeProgress?.rewardPending === 'player-cards') {
                handleMenuItemClick('challenge-reward');
              } else if (challengeProgress?.rewardPending === 'action-cards') {
                handleMenuItemClick('challenge-action-reward');
              } else {
                handleStartChallengeMatch(challengeProgress);
              }
            }}
            onStopChallenge={() => {
              resetChallengeProgress();
              handleMenuItemClick('menu');
            }}
          />
        )}
        {view === 'challenge-squad' && (
          <ChallengeSquadView
            progress={challengeProgress}
            onNavigate={handleMenuItemClick}
            onConfirm={(squad, cardTypeCounts) => {
              const updated = {
                ...challengeProgress,
                squad,
                cardTypeCounts,
                // preserve actionCards already earned; null only on first confirm
                actionCards: challengeProgress?.actionCards ?? null,
                enabledActionCards: challengeProgress?.enabledActionCards ?? null,
              };
              saveChallengeProgress(updated);
              setChallengeProgress(updated);
              handleMenuItemClick('challenge-action-cards');
            }}
          />
        )}
        {view === 'challenge-action-cards' && (
          <ChallengeActionCards
            progress={challengeProgress}
            matchupLabel={formatChallengeMatchup(tournamentData.find(r => r.Stage === String(challengeProgress?.level ?? 1)))}
            onNavigate={handleMenuItemClick}
            onUpdateEnabled={(enabledActionCards) => updateChallengeProgress({ enabledActionCards })}
            onStartMatch={(updatedProgress) => {
              updateChallengeProgress({
                actionCards: updatedProgress.actionCards,
                enabledActionCards: updatedProgress.enabledActionCards,
              });
              handleStartChallengeMatch(updatedProgress);
            }}
          />
        )}
        {view === 'challenge-reward' && (
          <ChallengeReward
            progress={challengeProgress}
            matchupLabel={formatChallengeMatchup(tournamentData.find(r => r.Stage === String((challengeProgress?.level ?? 1) + 1)))}
            onNavigate={handleMenuItemClick}
            onConfirm={(updatedSquad, updatedDraftedIds) => {
              updateChallengeProgress({ squad: updatedSquad, draftedRewardIds: updatedDraftedIds, rewardPending: 'action-cards' });
              handleMenuItemClick('challenge-action-reward');
            }}
          />
        )}
        {view === 'challenge-action-reward' && (
          <ChallengeActionCardReward
            progress={challengeProgress}
            matchupLabel={formatChallengeMatchup(tournamentData.find(r => r.Stage === String((challengeProgress?.level ?? 1) + 1)))}
            onStartMatch={(updatedProgress) => {
              // updatedProgress.level is nextLevel (already incremented in the reward screen).
              // If it's now 9 there is no Trial 9 — clear the run and return to Challenge menu.
              if (updatedProgress.level > 8) {
                resetChallengeProgress();
                handleMenuItemClick('challenge');
              } else {
                setChallengeProgress(updatedProgress);
                handleStartChallengeMatch(updatedProgress);
              }
            }}
          />
        )}
      </Suspense>
      </AppErrorBoundary>

      {/* Abandon Match Warning Modal */}
      {showAbandonWarning && (
        <div className="abandon-warning-overlay">
          <div className="abandon-warning-modal">
            <div className="abandon-warning-header">
              <h2 className="abandon-warning-title">
                {challengeMatchSquad ? '🏆 Leave Challenge Trial?' : '⚠️ Abandon Match?'}
              </h2>
            </div>
            <div className="abandon-warning-body">
              {challengeMatchSquad ? (
                <>
                  <p className="abandon-warning-message">
                    You are in <strong>Trial {challengeProgress?.level}</strong> of your Challenge. Leaving will end this match attempt — <strong style={{ color: '#f87171' }}>this attempt will count as a loss</strong>.
                  </p>
                  <p className="abandon-warning-message" style={{ color: '#86efac' }}>
                    ✅ Your challenge position is saved — return to the <strong>Challenge menu</strong> to retry Trial {challengeProgress?.level} from the action cards screen.
                  </p>
                </>
              ) : (
                <>
                  <p className="abandon-warning-message">
                    You are currently in a match. Leaving will abandon the current match and all progress will be lost.
                  </p>
                  <p className="abandon-warning-message">
                    Are you sure you want to abandon the match?
                  </p>
                </>
              )}
            </div>
            <div className="abandon-warning-actions">
              <button className="abandon-warning-button cancel" onClick={handleCancelAbandon}>
                Stay in Match
              </button>
              <button className="abandon-warning-button confirm" onClick={handleAbandonMatch}>
                {challengeMatchSquad ? 'Leave (Trial saved)' : 'Abandon Match'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Challenge Leave Warning — navigating away from reward / setup screens */}
      {showChallengeLeaveWarning && (() => {
        const level = challengeProgress?.level ?? 1;
        const msgs = {
          'challenge-reward':        { icon: '🎁', title: 'Reward Waiting!',      body: `You've won Trial ${level} and your player card reward is ready to pick. If you leave now, it will be saved — return via the Challenge menu to claim it.` },
          'challenge-action-reward': { icon: '⚡', title: 'Action Card Waiting!', body: `Your action card reward from Trial ${level} is ready. It will be saved — return via the Challenge menu to claim it.` },
          'challenge-action-cards':  { icon: '🏆', title: 'Trial Ready to Start', body: `Trial ${level} is set up and waiting. Your squad and action cards are saved — return via the Challenge menu to start the match.` },
          'challenge-squad':         { icon: '👥', title: 'Squad Setup in Progress', body: `You're setting up your squad for Trial ${level}. Your challenge run is saved — return via the Challenge menu to continue.` },
        };
        const { icon, title, body } = msgs[view] ?? { icon: '🏆', title: 'Challenge in Progress', body: `Your challenge progress is saved. Return via the Challenge menu to continue.` };
        return (
          <div className="abandon-warning-overlay">
            <div className="abandon-warning-modal">
              <div className="abandon-warning-header">
                <h2 className="abandon-warning-title">{icon} {title}</h2>
              </div>
              <div className="abandon-warning-body">
                <p className="abandon-warning-message">{body}</p>
                <p className="abandon-warning-message" style={{ color: '#86efac', marginTop: '8px' }}>
                  ✅ Progress is auto-saved — you won't lose your place.
                </p>
              </div>
              <div className="abandon-warning-actions">
                <button
                  className="abandon-warning-button cancel"
                  onClick={() => { setShowChallengeLeaveWarning(false); setChallengeLeaveTarget(null); }}
                >
                  Stay Here
                </button>
                <button
                  className="abandon-warning-button confirm"
                  onClick={() => {
                    setShowChallengeLeaveWarning(false);
                    setView(challengeLeaveTarget);
                    setChallengeLeaveTarget(null);
                    setMenuOpen(false);
                  }}
                >
                  Leave (Progress Saved)
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Game Mode Modal ── */}
      {showGameModeModal && (() => {
        const rawSquads = localStorage.getItem('kickopp-squads');
        const squads = rawSquads ? JSON.parse(rawSquads) : { 'Player 1': [], 'Player 2': [] };
        const p1Squad = squads['Player 1'] || [];
        const p2Squad = squads['Player 2'] || [];
        const p1Complete = checkSquadComplete(p1Squad);
        const p2Complete = checkSquadComplete(p2Squad);
        const squadsReady = p1Complete && p2Complete;

        const rawAC = localStorage.getItem('kickopp-action-selections');
        const acSelections = rawAC ? JSON.parse(rawAC) : { 'Player 1': [], 'Player 2': [] };
        const p1AC = acSelections['Player 1'] || [];
        const p2AC = acSelections['Player 2'] || [];
        const acReady = p1AC.length > 0 && p2AC.length > 0;

        const statusDot = (ok) => (
          <span style={{
            width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
            background: ok ? '#10b981' : '#ef4444',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '10px', fontWeight: 'bold', color: 'white'
          }}>{ok ? '✓' : '!'}</span>
        );

        // Card option component
        const ModeCard = ({ selected, onClick, disabled, color, icon, title, desc, tags, statusRows, navLink }) => (
          <div
            onClick={() => !disabled && onClick()}
            style={{
              flex: '1 1 200px', padding: '10px 14px', borderRadius: '14px',
              border: `2px solid ${selected ? color : disabled ? 'rgba(51,65,85,0.4)' : `${color}55`}`,
              background: selected
                ? `linear-gradient(135deg,${color}22,${color}0a)`
                : disabled ? 'rgba(15,23,42,0.4)' : `${color}0a`,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
              boxShadow: selected ? `0 0 0 1px ${color}44, 0 8px 24px ${color}22` : 'none',
            }}
          >
            {/* Centered content block grows to fill available space */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '5px', width: '100%' }}>
              <div style={{ fontSize: '20px', textAlign: 'center', lineHeight: 1 }}>{icon}</div>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: selected ? 'white' : '#cbd5e1', fontSize: '17px', fontWeight: '700', margin: '0 0 4px 0' }}>{title}</h3>
                <p style={{ color: '#64748b', fontSize: '12px', margin: 0, lineHeight: 1.5 }}>{desc}</p>
              </div>
              {statusRows && (
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '5px 8px', width: '100%' }}>
                  {statusRows}
                </div>
              )}
              {tags && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {tags.map((t, i) => (
                    <span key={i} style={{ fontSize: '11px', color: selected ? color : '#475569', fontWeight: '600',
                      padding: '2px 8px', borderRadius: '20px', background: selected ? `${color}22` : 'rgba(51,65,85,0.3)' }}>{t}</span>
                  ))}
                </div>
              )}
              {navLink && (
                <div style={{ width: '100%', marginTop: '6px' }}>
                  <p style={{ color: '#94a3b8', fontSize: '10px', textAlign: 'center', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                    Set up here to unlock this mode
                  </p>
                  <button
                    className="mode-nav-link"
                    onClick={(e) => { e.stopPropagation(); navLink.onClick(); }}
                    style={{
                      width: '100%', padding: '8px 0',
                      borderRadius: '10px', border: `2px solid ${color}`,
                      background: `${color}28`, color: color,
                      fontSize: '13px', fontWeight: '800', cursor: 'pointer',
                      letterSpacing: '0.5px', transition: 'all 0.15s',
                      boxShadow: `0 0 12px ${color}88`,
                    }}
                  >
                    ↗ {navLink.label}
                  </button>
                </div>
              )}
            </div>
            {/* SELECTED badge always at the bottom */}
            {selected && (
              <div style={{ textAlign: 'center', paddingTop: '4px', borderTop: `1px solid ${color}33`, width: '100%' }}>
                <span style={{ fontSize: '11px', color: color, fontWeight: '700', letterSpacing: '0.5px' }}>✓ SELECTED</span>
              </div>
            )}
          </div>
        );

        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            ...MENU_OVERLAY_BG,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px'
          }}>
            <MenuBg />
            <div style={{
              position: 'relative', zIndex: 1,
              background: 'linear-gradient(135deg,#1e293b,#0f172a)',
              border: '1px solid rgba(51,65,85,0.7)',
              borderRadius: '24px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
              padding: 'clamp(10px, 2vw, 20px) clamp(10px, 2vw, 28px)',
              width: 'min(860px, calc(100vw - 32px))',
              overflowY: 'auto', maxHeight: 'calc(100dvh - 32px)'
            }}>
              {/* Header */}
              <h2 style={{
                color: 'white', fontSize: '22px', fontWeight: '800',
                textAlign: 'center', letterSpacing: '2px', textTransform: 'uppercase',
                margin: '0 0 4px 0'
              }}>Choose Match Mode</h2>
              <p style={{ color: '#475569', fontSize: '12px', textAlign: 'center', margin: '0 0 10px 0' }}>
                Select how each section is set up — then start the match
              </p>

              {/* ── Section 1: Player Cards ── */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                  <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Player Cards</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(51,65,85,0.5)' }} />
                </div>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  <ModeCard
                    selected={gameMode === 'basic'}
                    onClick={() => setGameMode('basic')}
                    color="#3b82f6"
                    icon="🃏"
                    title="All Collection"
                    desc="All cards available. Both players draft from the same shared pool."
                    tags={['All cards', 'Shared pool', 'Always ready']}
                  />
                  <ModeCard
                    selected={gameMode === 'squads'}
                    onClick={() => squadsReady && setGameMode('squads')}
                    disabled={!squadsReady}
                    color="#10b981"
                    icon={squadsReady ? '⚽' : '🔒'}
                    title="Individual Squads"
                    desc="Each team plays with their own 44 cards. Manage squads in Player Cards."
                    tags={['44 cards each', 'Own deck']}
                    navLink={{ label: 'Open Player Cards', onClick: () => { setShowGameModeModal(false); setCardModalReturn(true); setView('cards'); } }}
                    statusRows={<>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        {statusDot(p1Complete)}
                        <span style={{ color: p1Complete ? '#86efac' : '#fca5a5', fontSize: '12px', fontWeight: '600' }}>
                          Player 1: {p1Squad.length}/44 {p1Complete ? '✓' : '— incomplete'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {statusDot(p2Complete)}
                        <span style={{ color: p2Complete ? '#86efac' : '#fca5a5', fontSize: '12px', fontWeight: '600' }}>
                          Player 2: {p2Squad.length}/44 {p2Complete ? '✓' : '— incomplete'}
                        </span>
                      </div>
                    </>}
                  />
                </div>
              </div>

              {/* ── Section 2: Action Cards ── */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                  <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Action Cards</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(51,65,85,0.5)' }} />
                </div>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  <ModeCard
                    selected={actionMode === 'basic'}
                    onClick={() => setActionMode('basic')}
                    color="#f59e0b"
                    icon="⚡"
                    title="All Action Cards"
                    desc="Full shared deck of all action cards available to both players."
                    tags={['All cards', 'Shared deck', 'Always ready']}
                  />
                  <ModeCard
                    selected={actionMode === 'individual'}
                    onClick={() => acReady && setActionMode('individual')}
                    disabled={!acReady}
                    color="#a855f7"
                    icon={acReady ? '🎴' : '🔒'}
                    title="Individual Action Cards"
                    desc="Each player uses their own selection. Manage cards in Action Cards."
                    tags={['Custom deck', 'Per player']}
                    navLink={{ label: 'Open Action Cards', onClick: () => { setShowGameModeModal(false); setCardModalReturn(true); setView('action-cards'); } }}
                    statusRows={<>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        {statusDot(p1AC.length > 0)}
                        <span style={{ color: p1AC.length > 0 ? '#86efac' : '#fca5a5', fontSize: '12px', fontWeight: '600' }}>
                          Player 1: {p1AC.length} action cards {p1AC.length > 0 ? '✓' : '— none selected'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {statusDot(p2AC.length > 0)}
                        <span style={{ color: p2AC.length > 0 ? '#86efac' : '#fca5a5', fontSize: '12px', fontWeight: '600' }}>
                          Player 2: {p2AC.length} action cards {p2AC.length > 0 ? '✓' : '— none selected'}
                        </span>
                      </div>
                    </>}
                  />
                </div>
              </div>

              {/* ── Section 3: Board Layout ── */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#06b6d4', flexShrink: 0 }} />
                  <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Board Layout</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(51,65,85,0.5)' }} />
                </div>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  <ModeCard
                    selected={verticalBoard}
                    onClick={() => setVerticalBoard(true)}
                    color="#8b5cf6"
                    icon="↕️"
                    title="Vertical"
                    desc="Chess-style portrait board — columns run top to bottom."
                    tags={['Portrait']}
                  />
                  <ModeCard
                    selected={!verticalBoard}
                    onClick={() => setVerticalBoard(false)}
                    color="#06b6d4"
                    icon="↔️"
                    title="Horizontal"
                    desc="Classic landscape board — columns run left to right."
                    tags={['Landscape']}
                  />
                </div>
              </div>

              {/* ── Play buttons ── */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleCancelGameMode}
                  style={{
                    padding: '11px 28px', borderRadius: '10px',
                    border: '1px solid rgba(51,65,85,0.5)',
                    background: 'transparent', color: '#64748b',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Only require a complete squad when Individual Squads mode is active
                    if (gameMode === 'squads') {
                      const rawSq = localStorage.getItem('kickopp-squads');
                      const sq = rawSq ? JSON.parse(rawSq) : { 'Player 1': [], 'Player 2': [] };
                      if (!checkSquadComplete(sq['Player 1'] || [])) {
                        setShowSoloSquadWarning(true);
                        return;
                      }
                    }
                    setShowHomeAwayModal(true);
                  }}
                  style={{
                    padding: '13px 36px', borderRadius: '12px', border: 'none',
                    cursor: 'pointer', fontWeight: '800', fontSize: '15px',
                    background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                    color: 'white', letterSpacing: '0.5px',
                    boxShadow: '0 6px 20px rgba(124,58,237,0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(124,58,237,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(124,58,237,0.4)'; }}
                >
                  🤖 Play Solo vs CPU
                </button>
                <button
                  onClick={() => handleSelectGameMode(gameMode, actionMode)}
                  style={{
                    padding: '13px 48px', borderRadius: '12px', border: 'none',
                    cursor: 'pointer', fontWeight: '800', fontSize: '16px',
                    background: 'linear-gradient(135deg,#3b82f6,#a855f7)',
                    color: 'white', letterSpacing: '0.5px',
                    boxShadow: '0 6px 20px rgba(59,130,246,0.4)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(59,130,246,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(59,130,246,0.4)'; }}
                >
                  ⚽ Play Local PvP
                </button>
              </div>

              {/* summary line */}
              <p style={{ textAlign: 'center', color: '#475569', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                {gameMode === 'basic' ? 'All Collection' : 'Individual Squads'} · {actionMode === 'basic' ? 'All Action Cards' : 'Individual Action Cards'} · {verticalBoard ? 'Vertical Board' : 'Horizontal Board'}
              </p>
            </div>
          </div>
        );
      })()}


      {/* ── Home / Away Selection Modal (Solo mode) ── */}
      {showHomeAwayModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          ...MENU_OVERLAY_BG,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <MenuBg />
          <div style={{
            position: 'relative', zIndex: 1,
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            border: '1px solid rgba(124,58,237,0.4)',
            borderRadius: '24px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 40px rgba(124,58,237,0.2)',
            padding: '40px 48px',
            textAlign: 'center',
            minWidth: '380px'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🤖</div>
            <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 6px 0' }}>
              Solo vs CPU
            </h2>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 28px 0' }}>
              Choose which side you want to play
            </p>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
              <button
                onClick={() => handleStartSolo(2)}
                style={{
                  padding: '16px 28px', borderRadius: '14px', border: '2px solid #3b82f6',
                  background: 'rgba(59,130,246,0.12)', color: 'white',
                  cursor: 'pointer', fontWeight: '700', fontSize: '14px',
                  transition: 'all 0.2s ease', minWidth: '140px'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>🏠</div>
                <div style={{ color: '#60a5fa', letterSpacing: '1px' }}>PLAY HOME</div>
                <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>Team 1 · Goes first</div>
              </button>
              <button
                onClick={() => handleStartSolo(1)}
                style={{
                  padding: '16px 28px', borderRadius: '14px', border: '2px solid #ef4444',
                  background: 'rgba(239,68,68,0.12)', color: 'white',
                  cursor: 'pointer', fontWeight: '700', fontSize: '14px',
                  transition: 'all 0.2s ease', minWidth: '140px'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>✈️</div>
                <div style={{ color: '#f87171', letterSpacing: '1px' }}>PLAY AWAY</div>
                <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>Team 2 · Goes second</div>
              </button>
            </div>
            <button
              onClick={() => setShowHomeAwayModal(false)}
              style={{
                marginTop: '20px', background: 'none', border: 'none',
                color: '#475569', fontSize: '13px', cursor: 'pointer'
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      )}

      {/* ── Difficulty + Win Mode Selection Modal (Solo mode) ── */}
      {showStageModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          ...MENU_OVERLAY_BG,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <MenuBg />
          <div style={{
            position: 'relative', zIndex: 1,
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            border: '1px solid rgba(124,58,237,0.4)',
            borderRadius: '24px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
            padding: 'clamp(14px, 3vw, 28px) clamp(14px, 3vw, 36px)',
            width: 'min(560px, calc(100vw - 32px))',
            overflowY: 'auto', maxHeight: 'calc(100dvh - 32px)'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '30px', marginBottom: '6px' }}>🎯</div>
              <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 3px 0' }}>
                Select Difficulty
              </h2>
              <p style={{ color: '#475569', fontSize: '11px', margin: 0 }}>
                Choose your CPU opponent's tactic and strength
              </p>
            </div>

            {/* Difficulty rows — compact */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '18px' }}>
              {tournamentData.map((row) => {
                const tendency = row.Tendency?.trim();
                const tactic = row['Tactic ']?.trim() || row.Tactic?.trim();
                const stageNum = row.Stage;
                const stageIdx = parseInt(stageNum) || 1;
                const isSelected = selectedStage?.Stage === stageNum;
                const tendencyColor = tendency === 'Attacking' || tendency === 'Attacking '
                  ? '#f97316'
                  : tendency === 'Defensive'
                  ? '#3b82f6'
                  : '#64748b';
                return (
                  <div
                    key={stageNum}
                    onClick={() => setSelectedStage(row)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '7px 12px', borderRadius: '10px',
                      border: `1.5px solid ${isSelected ? tendencyColor : 'rgba(51,65,85,0.4)'}`,
                      background: isSelected ? `${tendencyColor}14` : 'rgba(15,23,42,0.45)',
                      cursor: 'pointer', transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = `${tendencyColor}55`; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(51,65,85,0.4)'; }}
                  >
                    <div style={{
                      width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
                      background: isSelected ? tendencyColor : 'rgba(51,65,85,0.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: '800', color: 'white'
                    }}>{stageNum}</div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: isSelected ? 'white' : '#cbd5e1', fontWeight: '700', fontSize: '13px' }}>{tactic}</span>
                      <div>
                        {Array.from({ length: 8 }, (_, i) => (
                          <span key={i} style={{ fontSize: '8px', color: i < stageIdx ? tendencyColor : 'rgba(51,65,85,0.45)', marginRight: '1px' }}>★</span>
                        ))}
                      </div>
                    </div>
                    <div style={{
                      padding: '2px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '700',
                      letterSpacing: '0.4px', color: tendencyColor,
                      background: `${tendencyColor}1a`, border: `1px solid ${tendencyColor}40`
                    }}>{tendency}</div>
                    {isSelected && (
                      <div style={{ color: tendencyColor, fontWeight: '800', fontSize: '12px' }}>✓</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Win Mode selector */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(51,65,85,0.5)' }} />
                <span style={{ color: '#64748b', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  Win Condition
                </span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(51,65,85,0.5)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {/* Win option */}
                {[
                  {
                    id: 'win',
                    icon: '⚽',
                    label: 'Win',
                    sub: 'Eliminate more CPU cards than the CPU in 20 turns',
                    color: '#10b981',
                  },
                  {
                    id: 'superwin',
                    icon: '⚡',
                    label: 'Super Win',
                    sub: 'Eliminate ALL 12 CPU cards in 20 turns',
                    color: '#fbbf24',
                  },
                ].map(opt => {
                  const active = selectedMatchMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setSelectedMatchMode(opt.id)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                        padding: '12px 10px', borderRadius: '12px', border: 'none',
                        border: `2px solid ${active ? opt.color : 'rgba(51,65,85,0.4)'}`,
                        background: active ? `${opt.color}15` : 'rgba(15,23,42,0.5)',
                        cursor: 'pointer', transition: 'all 0.15s ease',
                        textAlign: 'center',
                      }}
                    >
                      <span style={{ fontSize: '22px', lineHeight: 1 }}>{opt.icon}</span>
                      <span style={{
                        fontWeight: '800', fontSize: '13px', letterSpacing: '0.5px',
                        color: active ? opt.color : '#94a3b8',
                      }}>{opt.label}</span>
                      <span style={{
                        fontSize: '10px', lineHeight: '1.4', color: '#475569',
                        fontWeight: '500',
                      }}>{opt.sub}</span>
                      {active && (
                        <span style={{ fontSize: '10px', color: opt.color, fontWeight: '800' }}>✓ Selected</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { setShowStageModal(false); setShowHomeAwayModal(true); }}
                style={{
                  padding: '11px 28px', borderRadius: '10px',
                  border: '1px solid rgba(51,65,85,0.5)',
                  background: 'transparent', color: '#64748b',
                  fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                }}
              >← Back</button>
              <button
                onClick={() => selectedStage && handleStartWithStage(selectedStage)}
                disabled={!selectedStage}
                style={{
                  padding: '13px 40px', borderRadius: '12px', border: 'none',
                  cursor: selectedStage ? 'pointer' : 'default',
                  fontWeight: '800', fontSize: '15px',
                  background: selectedStage
                    ? 'linear-gradient(135deg,#7c3aed,#4f46e5)'
                    : 'rgba(51,65,85,0.4)',
                  color: selectedStage ? 'white' : '#475569',
                  letterSpacing: '0.5px',
                  boxShadow: selectedStage ? '0 6px 20px rgba(124,58,237,0.4)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >⚽ Start Match</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Solo Squad Warning Modal ── */}
      {showSoloSquadWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10002,
          ...MENU_OVERLAY_BG,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <MenuBg />
          <div style={{
            position: 'relative', zIndex: 1,
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            border: '1px solid rgba(239,68,68,0.5)',
            borderRadius: '24px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 40px rgba(239,68,68,0.15)',
            padding: '40px 48px',
            textAlign: 'center',
            maxWidth: '420px',
            width: '90%'
          }}>
            <div style={{ fontSize: '44px', marginBottom: '14px' }}>⚠️</div>
            <h2 style={{
              color: 'white', fontSize: '18px', fontWeight: '800',
              letterSpacing: '1.5px', textTransform: 'uppercase', margin: '0 0 12px 0'
            }}>
              Team 1 Squad Incomplete
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.7', margin: '0 0 28px 0' }}>
              To play vs CPU you need a complete Team 1 squad of <strong style={{ color: 'white' }}>44 players</strong> (4 Keepers, 10 DC, 6 DW, 10 MC, 6 MW, 8 Forwards).
              <br /><br />
              Set up your squad in the <strong style={{ color: '#60a5fa' }}>Cards Collection</strong> first.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowSoloSquadWarning(false)}
                style={{
                  padding: '11px 24px', borderRadius: '10px',
                  border: '1px solid rgba(51,65,85,0.6)',
                  background: 'transparent', color: '#64748b',
                  fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGoToCardsCollection}
                style={{
                  padding: '12px 28px', borderRadius: '12px', border: 'none',
                  cursor: 'pointer', fontWeight: '800', fontSize: '14px',
                  background: 'linear-gradient(135deg,#3b82f6,#2563eb)',
                  color: 'white', letterSpacing: '0.3px',
                  boxShadow: '0 6px 20px rgba(59,130,246,0.4)'
                }}
              >
                Go to Cards Collection →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Button — temporarily hidden for this version */}
      {/* {!['challenge', 'challenge-reward', 'challenge-action-reward'].includes(view) && <FeedbackButton />} */}

      {/* Sound Toggle Button — temporarily hidden for this version */}
      {/* {!['challenge', 'challenge-reward', 'challenge-action-reward'].includes(view) && (
        <button
          className="sound-toggle-btn"
          onClick={toggleSound}
          title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
        >
          <span className="sound-icon">{soundEnabled ? '🔊' : '🔇'}</span>
          <span className="sound-text">{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
        </button>
      )} */}
    </div>
  );
  // Arena keeps the 1.1× web zoom; all other pages use 1.0 (no extra zoom)
  return (
    <>
      <GameViewport zoom={isArena ? undefined : 1.0}>{appContent}</GameViewport>
      <Analytics />
    </>
  );
}

export default App;
